import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Org, Role } from '../../../enum';
import { ManagerPage } from '../../../enum';
import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { fetchSuperAdmins } from '../super-admin/superAdminSlice';
import { validateAdminManager } from './admin-managers.helper';
import type { ResetPassword } from './adminManagerPasswordReset';

const { logError } = RaygunErrorHandlerService();

export class AdminManager {
  id: string | undefined = undefined;
  full_name: string | undefined = undefined;
  first_name: string | undefined = undefined;
  last_name: string | undefined = undefined;
  email: string | undefined = undefined;
  pass?: string | undefined = undefined;
  mobile_phone: string | null | undefined = undefined;
  created: string | undefined = undefined;
  role: Role | null = null;
  account_status: string | undefined = undefined;
  org_code: Org | null = null;
  org_name: string | undefined = undefined;
  org_timezone_name: string | undefined = undefined;
  total_count?: number | undefined = undefined;

  // Regional manager
  regions?: string[] = [];
  region_numbers?: string[] = [];

  // FirstAlerts manager
  first_alerts_community_id?: string | null;
  first_alerts_community_name?: string;

  // Service provider manager
  service_provider_organization_id?: string | null;
  service_provider_organization_name?: string;
}

export const ADMIN_MANAGER_MODAL = {
  PASSWORD_RESET: 'password_reset',
  IDLE: 'idle',
  EDIT_MANAGER: 'edit_manager',
} as const;

export type ADMIN_MANAGER_MODAL = (typeof ADMIN_MANAGER_MODAL)[keyof typeof ADMIN_MANAGER_MODAL];

type FirstAlertCommunity = {
  id: string;
  name: string;
  type_id: string;
  type_name: string;
  org_code: Org;
};

export type Region = {
  name: string;
  number: number;
};

type AdminManagerSliceType = {
  managers: AdminManager[];
  regions: Region[];
  requestAdminManagerParam: RequestParams<AdminManager>;
  adminManagerApiStatus: APIStatus;
  adminManagerModal: ADMIN_MANAGER_MODAL;
  adminManager: AdminManager;
  firstAlertsCommunities: FirstAlertCommunity[];
};

const initialState: AdminManagerSliceType = {
  managers: [],
  regions: [],
  requestAdminManagerParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminManager>('full_name', 'ascend'),
    filteredInfo: {},
  },
  adminManagerApiStatus: APIStatus.IDLE,
  adminManagerModal: ADMIN_MANAGER_MODAL.IDLE,
  adminManager: new AdminManager(),
  firstAlertsCommunities: [],
};

const managersEndpoint = {
  [ManagerPage.LEPSManager]: 'v0_get_managers',
  [ManagerPage.FirstAlertsManager]: 'v0_get_first_alerts_managers',
  [ManagerPage.ServiceProviderManager]: 'v0_get_service_provider_managers',
  [ManagerPage.Admin]: '', // Only super admins can access the admin page
};

export const fetchAdminManagers = createAsyncThunk<AdminManager[], ManagerPage, AsyncThunkConfig>(
  'adminManager/fetchAdminManagers',
  async (type, thunkAPI) => {
    try {
      const requestAdminManagerParam = thunkAPI.getState().adminManagerSlice.requestAdminManagerParam;
      const response = (await axios.post(managersEndpoint[type], requestAdminManagerParam)) as AdminManager[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminManagerSlice', 'fetchAdminManagers', type]);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchAllRegions = createAsyncThunk<Region[], undefined, AsyncThunkConfig>(
  'adminManager/fetchAllRegions',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.get('v0_get_all_regions')) as Region[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminManagerSlice', 'fetchAllRegions']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchAllFACommunities = createAsyncThunk<FirstAlertCommunity[], undefined, AsyncThunkConfig>(
  'adminManager/fetchAllFACommunities',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_get_admin_first_alerts_communities')) as FirstAlertCommunity[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminManagerSlice', 'fetchAllFACommunities']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const adminManagerPasswordReset = createAsyncThunk<string, ResetPassword, AsyncThunkConfig>(
  'adminManager/adminManagerPasswordReset',
  async (resetPassword, thunkAPI) => {
    try {
      const response = (await axios.post('v0_set_manager_password', resetPassword)) as string; // id;
      if (response) {
        notification.success({
          title: 'Success',
          description: 'Password set successfully.',
        });
      }
      return response;
    } catch (e) {
      logError(e, ['adminManagerSlice', 'adminManagerPasswordReset']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type SetAdminManagerStatusData = {
  manager_id: string;
  status: string;
  type: ManagerPage;
};

export const setAdminManagerStatus = createAsyncThunk<string, SetAdminManagerStatusData, AsyncThunkConfig>(
  'adminManager/setAdminManagerStatus',
  async ({ type, ...data }, thunkAPI) => {
    try {
      const response = (await axios.post('v0_manager_status_set', data)) as string; // id;
      if (response) {
        notification.success({
          title: 'Success',
          description: `Your manager has been successfully ${data.status === 'active' ? 'unarchived' : 'archived'}.`,
        });
        if (type === ManagerPage.Admin) {
          thunkAPI.dispatch(fetchSuperAdmins());
        } else {
          thunkAPI.dispatch(fetchAdminManagers(type));
        }
      }
      return response;
    } catch (e) {
      logError(e, ['adminManagerSlice', 'setAdminManagerStatus']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

class AdminManagerUpsertResponse {
  manager_id: string = '';
  is_update: boolean = false;
}

export const upsertAdminManager = createAsyncThunk<
  AdminManagerUpsertResponse,
  { upsertAdminManager: AdminManager; type: ManagerPage },
  AsyncThunkConfig
>('adminManager/upsertAdminManager', async ({ upsertAdminManager, type }, thunkAPI) => {
  try {
    const currentUserRole = thunkAPI.getState().authSlice.currentUser.role;
    if (validateAdminManager(upsertAdminManager, currentUserRole)) {
      const response = (await axios.post('v0_manager_upsert', upsertAdminManager)) as AdminManagerUpsertResponse;
      if (response) {
        thunkAPI.dispatch(fetchAdminManagers(type));
        thunkAPI.dispatch(closeAdminManagerModal());
        notification.success({
          title: `Manager ${response.is_update ? 'updated' : 'added'} successfully`,
          description: 'Success',
        });
        return response;
      }
    }
    return new AdminManagerUpsertResponse();
  } catch (e) {
    logError(e, ['adminManagerSlice', 'upsertAdminManager']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const adminManagerSlice = createSlice({
  name: 'adminManager',
  initialState,
  reducers: {
    handleManagerTableChange: (state, action: PayloadAction<TableChange<AdminManager>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminManager>;
      state.requestAdminManagerParam.pagination = action.payload.pagination;
      state.requestAdminManagerParam.sortedInfo = sorter;
      state.requestAdminManagerParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestAdminManagerParam.pagination = initialState.requestAdminManagerParam.pagination;
      state.requestAdminManagerParam.sortedInfo = initialState.requestAdminManagerParam.sortedInfo;
      state.requestAdminManagerParam.filteredInfo = initialState.requestAdminManagerParam.filteredInfo;
    },
    showEditModal: (state, action: PayloadAction<ADMIN_MANAGER_MODAL>) => {
      state.adminManagerModal = action.payload;
    },
    setAdminManager: (state, action: PayloadAction<AdminManager>) => {
      state.adminManager = action.payload;
    },
    closeAdminManagerModal: (state) => {
      state.adminManagerModal = ADMIN_MANAGER_MODAL.IDLE;
      state.adminManager = new AdminManager();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminManagers.pending, (state, _action) => {
        state.adminManagerApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAdminManagers.fulfilled, (state, action) => {
        state.managers = action.payload;
        state.adminManagerApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchAdminManagers.rejected, (state, _action) => {
        state.adminManagerApiStatus = APIStatus.ERROR;
      })
      .addCase(adminManagerPasswordReset.fulfilled, (state) => {
        state.adminManagerModal = ADMIN_MANAGER_MODAL.IDLE;
      })
      .addCase(fetchAllFACommunities.fulfilled, (state, action) => {
        state.firstAlertsCommunities = action.payload;
      })
      .addCase(fetchAllRegions.fulfilled, (state, action) => {
        state.regions = action.payload;
      });
  },
});

export const { handleManagerTableChange, clearFilter, showEditModal, setAdminManager, closeAdminManagerModal } =
  adminManagerSlice.actions;
